import React, { ReactElement } from "react";
import { SettingsChannelEmailAlias, useGetChannelEmailsQuery } from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface EmailAliasesPickerProps<TIsMulti = false, TIsClearable = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string),
        display: (TIsMulti extends true ? string[] : string)
    ) => void;
}

const EmailAliases = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, isClearable,
}: EmailAliasesPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetChannelEmailsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<SettingsChannelEmailAlias>[] : Option<SettingsChannelEmailAlias>;
    type ValueType = TIsMulti extends true ? Option<SettingsChannelEmailAlias>[] : Option<SettingsChannelEmailAlias>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<SettingsChannelEmailAlias>).value) as Parameters<typeof onSelected>[0];
        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label as string)
            : (selected as Option<SettingsChannelEmailAlias>).label) as Parameters<typeof onSelected>[1];
        onSelected(values, texts);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.settings?.channelEmail?.emails?.map(email => ({
        label: `${email.originalDestinationEmail} ${email.isDefault ? "(Default)" : ""}`,
        value: email.id,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={values}
            isClearable={isClearable}
            value={selected as ValueType}
        />
    );
});

EmailAliases.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: false,
};

export default EmailAliases as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
> (
    props: EmailAliasesPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof EmailAliases>;
